
import { useEffect, useState } from "react";
import { IoWalletOutline } from "react-icons/io5";
import { MdOutlinePayments } from "react-icons/md";
import Loading from "../../icons/Loading";
import useGroup from "../../hook/useGroup";
import Cookies from "js-cookie";
import { getWalletByTenantId } from "../../services/wallet";
import { getMyCdirCreditCardWithTenantIdAndIsActive } from "../../services/BillingCreditCard";
import { getMyCdirACHInfoWithTenantIdAndIsActive } from "../../services/ACHInfo";
import { INVOICE_TYPES, InvoiceType } from "./Billing";

type WalletProps = {
    rechargeAmount: string;
    setRechargeAmount: (value: string) => void;
    isRefreshWalletDetails: boolean;
    setIsRefreshWalletDetails: (value: boolean) => void;
    paymentType: number,
    setPaymentType: (value: number) => void;
    isProcessing: boolean;
    setIsProcessing: (value: boolean) => void;
    setShowPopupForPayment: (Value: boolean) => void;
    setInvoiceType: (value: InvoiceType) => void;
    setActivePaymentDetails: (value: any | null) => void;
    callTheAPIToRechargeTheWallet: (amount: number, paymentDetails: any, cb: any) => void;
    currency?: string;
};

const Wallet = ({
    rechargeAmount,
    setRechargeAmount,
    isRefreshWalletDetails,
    setIsRefreshWalletDetails,
    paymentType,
    setPaymentType,
    isProcessing,
    setIsProcessing,
    setShowPopupForPayment,
    setInvoiceType,
    setActivePaymentDetails,
    callTheAPIToRechargeTheWallet,
    currency = "$"
}: WalletProps) => {
    const { selectedTenantId, isSuperAdmin }: any = useGroup();
    const tenantId = isSuperAdmin
        ? selectedTenantId
        : Cookies.get("tenantId-my-cdir");

    const [walletDetails, setWalletDetails] = useState<any>({
        balance: 0,
        currency: "USD",
        lowBalanceThreshold: 10
    });
    const [showRecharge, setShowRecharge] = useState<boolean>(false);
    const [showPaymentDialog, setShowPaymentDialog] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!tenantId) return;

        if (!isRefreshWalletDetails) return;

        let isMounted = true;

        setLoading(true);

        getWalletByTenantId(tenantId)
            .then(res => {
                if (isMounted) {
                    setWalletDetails(res);
                }
            })
            .catch(error => {
                console.error("Failed to refresh wallet:", error);
            })
            .finally(() => {
                if (isMounted) {
                    setLoading(false);
                    setIsRefreshWalletDetails(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [tenantId, isRefreshWalletDetails, setIsRefreshWalletDetails]);

    const handleRecharge = async () => {
        const amount = Number(rechargeAmount);

        if (!amount || amount <= 0) {
            return;
        }

        setShowRecharge(false);
        setShowPaymentDialog(true);
    }

    const rechargeWallet = () => {
        if (tenantId) {
            const amount = Number(rechargeAmount);

            if (paymentType === 0) {
                getMyCdirCreditCardWithTenantIdAndIsActive(tenantId)
                    .then(res => {
                        if (isProcessing) return;
                        setIsProcessing(true);

                        callTheAPIToRechargeTheWallet(amount, res, () => {
                            setShowPaymentDialog(false);
                        });
                    })
                    .catch(error => {
                        console.log(error);
                        setActivePaymentDetails(null);
                        setShowPaymentDialog(false);
                        setInvoiceType(INVOICE_TYPES.WalletRecharge);

                        setShowPopupForPayment(true);
                    });
            } else {
                getMyCdirACHInfoWithTenantIdAndIsActive(tenantId)
                    .then(res => {
                        if (isProcessing) return;
                        setIsProcessing(true);

                        callTheAPIToRechargeTheWallet(amount, res, () => {
                            setShowPaymentDialog(false);
                        });
                    })
                    .catch(error => {
                        console.log(error);
                        setActivePaymentDetails(null);
                        setShowPaymentDialog(false);
                        setInvoiceType(INVOICE_TYPES.WalletRecharge);

                        setShowPopupForPayment(true);
                    });
            }
        }
    }

    const selectAmount = (amount: number) => {
        setRechargeAmount(amount.toString());
    }

    return (
        <>
            <div className="w-full border border-secondary rounded-md bg-primary">
                <div className="flex items-center justify-between bg-secondary px-4 py-3">
                    <div className="flex items-center gap-2 text-white">
                        <IoWalletOutline className="text-xl" />

                        <h4 className="font-medium text-sm xm:text-base">
                            Wallet
                        </h4>
                    </div>
                </div>

                {
                    loading
                        ? (
                            <div className="flex flex-col items-center justify-center py-12 gap-2">
                                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#2B3998]" />
                                <span className="text-sm text-gray-500">
                                    Loading...
                                </span>
                            </div>
                        ) : (
                            <div className="p-4">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                                    <div>
                                        <p className="text-xs text-gray-500">
                                            Available Balance
                                        </p>

                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-2xl xm:text-3xl font-semibold text-[#181842]">
                                                {currency}{" "}
                                                {walletDetails?.balance?.toFixed(2)}
                                            </span>
                                        </div>

                                        <p className="text-xs text-gray-400 mt-1">
                                            Available for account usage
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        {
                                            walletDetails?.balance <= walletDetails?.lowBalanceThreshold
                                                ? (
                                                    <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                                                        Low Balance
                                                    </span>
                                                ) : (
                                                    <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                                                        Wallet Active
                                                    </span>
                                                )
                                        }

                                        <button
                                            type="button"
                                            onClick={() => {
                                                setRechargeAmount("");
                                                setShowRecharge(true);
                                            }}
                                            className="flex items-center justify-center gap-2 bg-secondary hover:bg-custom-blue px-4 py-2 rounded-md text-white text-sm font-medium"
                                        >
                                            <MdOutlinePayments className="text-lg" />
                                            Recharge Wallet
                                        </button>

                                    </div>

                                </div>

                            </div>
                        )
                }
            </div>


            {
                showPaymentDialog && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">

                        <div className="w-full max-w-[400px] rounded-xl bg-white p-6 shadow-2xl animate-fadeIn">

                            <h3 className="text-lg font-semibold mb-5 text-[#181842]">
                                Select Payment Method
                            </h3>

                            <div className="flex flex-col gap-4">

                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="payment"
                                        className="accent-[#167AC3] w-4 h-4"
                                        checked={paymentType === 0}
                                        onChange={() => setPaymentType(0)}
                                    />

                                    <span className="text-sm text-gray-700">
                                        Credit Card
                                    </span>
                                </label>

                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="payment"
                                        className="accent-[#167AC3] w-4 h-4"
                                        checked={paymentType === 1}
                                        onChange={() => setPaymentType(1)}
                                    />

                                    <span className="text-sm text-gray-700">
                                        Bank ACH
                                    </span>
                                </label>
                            </div>

                            <div className="flex justify-end gap-3 mt-8">

                                <button
                                    type="button"
                                    onClick={rechargeWallet}
                                    className="min-w-[100px] rounded-md bg-[#167AC3] px-5 py-2 text-sm font-medium text-white transition hover:bg-[#1267a7]"
                                >
                                    {
                                        isProcessing
                                            ? <Loading />
                                            : "Continue"
                                    }
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setShowPaymentDialog(false)}
                                    className="min-w-[100px] rounded-md border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                showRecharge && (
                    <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 px-4 pt-2">
                        <div className="w-full max-w-[430px] rounded-xl bg-white shadow-xl">
                            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                                <div className="flex items-center gap-2">
                                    <IoWalletOutline className="text-xl text-secondary" />

                                    <h3 className="text-lg font-semibold text-[#181842]">
                                        Recharge Wallet
                                    </h3>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowRecharge(false);
                                        setRechargeAmount("");
                                    }}
                                    className="text-gray-400 hover:text-red-500 text-xl"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="px-5 py-5">
                                <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
                                    <p className="text-xs text-gray-500">
                                        Current Balance
                                    </p>

                                    <p className="text-2xl font-semibold text-[#181842] mt-1">
                                        {currency}{" "}
                                        {walletDetails?.balance?.toFixed(2)}
                                    </p>
                                </div>

                                <div className="mt-5">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Amount
                                    </label>

                                    <div className="grid grid-cols-3 gap-2">
                                        {
                                            [25, 50, 100].map((amount) => (
                                                <button
                                                    key={amount}
                                                    type="button"
                                                    onClick={() => selectAmount(amount)}
                                                    className={`rounded-md border px-3 py-2 text-sm font-medium transition ${Number(
                                                        rechargeAmount
                                                    ) === amount
                                                        ? "border-secondary bg-secondary text-white"
                                                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                                        }`}
                                                >
                                                    {currency} {amount}
                                                </button>
                                            ))
                                        }
                                    </div>
                                </div>

                                <div className="mt-5">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Recharge Amount
                                    </label>

                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                            $
                                        </span>

                                        <input
                                            type="number"
                                            min="1"
                                            step="0.01"
                                            value={rechargeAmount}
                                            onChange={(e) => setRechargeAmount(e.target.value)}
                                            placeholder="Enter amount"
                                            className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm outline-none focus:border-secondary"
                                        />
                                    </div>
                                </div>

                                {
                                    Number(rechargeAmount) > 0 && (
                                        <div className="mt-4 rounded-md bg-green-50 border border-green-200 px-4 py-3">

                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-600">
                                                    New Balance
                                                </span>

                                                <span className="font-semibold text-green-700">
                                                    {currency}{" "}
                                                    {(
                                                        walletDetails?.balance +
                                                        Number(rechargeAmount)
                                                    )?.toFixed(2)}
                                                </span>
                                            </div>

                                        </div>
                                    )
                                }
                            </div>

                            <div className="flex gap-3 border-t border-gray-200 px-5 py-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowRecharge(false);
                                        setRechargeAmount("");
                                    }}
                                    className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    disabled={!rechargeAmount || Number(rechargeAmount) <= 0}
                                    onClick={handleRecharge}
                                    className="w-full rounded-md bg-secondary px-4 py-2 text-sm font-medium text-white hover:bg-custom-blue disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Recharge
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </>
    );
};

export default Wallet;
